// Jeff's purchasing dashboard (ERP-PLAN §5.4): bought sell items by bucket, grouped by vendor with the
// order's totals against MOQ; each row has cover, forecast, available / incoming, the suggested quantity
// (editable), the order-by date and why. Picked lines go onto each vendor's open draft PO (for that ship-to), or a
// new one, in one click. Typed quantities and unticked lines are kept in this browser (lib/plan-work.ts).
// The plan is shared/planning.ts, worked out in the browser (web/src/lib/planning.ts), so nothing waits.
// ?bucket= picks the tab, ?vendor= filters to one vendor, ?item= opens an item's detail, ?guessed=1 shows only rows
// with guessed inputs (PlanFix.tsx: fill them in from each chip, or several at once from the bulk bar).
// Keys: j/k move between rows (the detail follows while it's open), Space ticks, Enter opens, / filters.
// Make planning --- parts in make items' BOMs are here too (their demand is the builds'), grouped by vendor and
// ship-to, so consigned parts go on POs to the CM that uses them (RM2 → Tailhoo). Builds are on the Make plan.

import { useVirtualizer } from "@tanstack/react-virtual";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Eye,
  EyeOff,
  FilePlus2,
  Info,
  Moon,
  RotateCcw,
  Search,
  Settings2,
  ShoppingCart,
  Wand2,
  X,
} from "lucide-react";
import { Fragment, memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { numberOf } from "../../../shared/calc.ts";
import type { Po } from "../../../shared/db.gen.ts";
import {
  addPlanDays,
  BUCKET_LABELS,
  type Bucket,
  coverText,
  explain,
  type FlagCode,
  fmt,
  groupByVendor,
  type ItemPlanResult,
  inBucket,
  monthLabel,
  orderByText,
  type Plan,
  shortDate,
  type VendorGroup,
} from "../../../shared/planning.ts";
import { priceFor, qtyWarnings } from "../../../shared/po.ts";
import { stockNote } from "../../../shared/stock.ts";
import { dayText, money, plural } from "../lib/format.ts";
import { can } from "../lib/mutate.ts";
import type { QtyEdit } from "../lib/plan-work.ts";
import {
  addToDraftPo,
  createDraftPo,
  dropStaleEdits,
  isEdited,
  qtyOf,
  saveItemPlan,
  saveSettings,
  setQtyEdit,
  setTicked,
  usePlan,
  useQtyEdits,
  useUnticked,
} from "../lib/planning.ts";
import { Link, setSearch, useSearch } from "../lib/router.tsx";
import { useRow, useRows } from "../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Input, Kbd, Select, Thumb } from "../ui/controls.tsx";
import { ExportButtons } from "../ui/ExportButtons.tsx";
import { isTyping, PageSearch, useStacked } from "../ui/ListPage.tsx";
import { Popover } from "../ui/Popover.tsx";
import { Fields } from "../ui/RecordPanel.tsx";
import { PaneLink, paneIsOpen } from "../ui/SidePane.tsx";
import { StockNumber } from "../ui/StockEditor.tsx"; // Stock changes ---
import { type LayoutColumn, useColumnLayout } from "../ui/TableViews.tsx"; // Table views ---
import { toast } from "../ui/toast.tsx";
import { ClosureChip, ClosuresButton } from "./Closures.tsx";
import { CoverThrough } from "./CoverThrough.tsx"; // Cover through ---
import { CutoverBanner } from "./Cutover.tsx";
import { ForecastSection } from "./ForecastSection.tsx";
import { BUCKET_TONE, ItemPlanSection, SnoozeForm } from "./ItemPlanSection.tsx";
import { SalesSection } from "./ItemSales.tsx";
import { itemPath } from "./ItemsPage.tsx";
import { AutoRunButton } from "./make/AutoRun.tsx"; // Make planning ---
import { PlanDetail, WhyButton } from "./PlanDetail.tsx";
import { BulkBar, FlagChip, GUESSED, isGuessed } from "./PlanFix.tsx";
import { openDraftFor, poPath } from "./po/lib.ts";
import { StockStaleBanner } from "./SyncHealth.tsx";

type Tab = Exclude<Bucket, "ok"> | "all";
const TABS: Tab[] = ["order_now", "at_risk", "this_month", "upcoming", "overstock", "snoozed", "not_planned", "all"];
/** Totals add up float prices; show them to the cent (money() keeps up to 4 places for unit prices). */
const cents = (n: number) => Math.round(n * 100) / 100;
const TAB_LABELS: Record<Tab, string> = { ...BUCKET_LABELS, all: "All" };
const TAB_HINTS: Record<Tab, string> = {
  order_now: "Order-by date is within the review period (or already passed)",
  at_risk: "Stock runs out before an open PO lands, or a PO is past due",
  this_month: "Order-by date in the next 30 days",
  upcoming: "Order-by date within the planning horizon",
  overstock: "More cover than the overstock limit, or stock with no sales",
  snoozed: "Snoozed by someone, with a reason",
  not_planned: "Hidden (discontinued…), end of life, untracked or pre-order: Show again on a hidden one's row",
  all: "Every planned item",
};
const EMPTY: Record<Tab, string> = {
  order_now: "Nothing to order now",
  at_risk: "Nothing at risk",
  this_month: "Nothing due this month",
  upcoming: "Nothing upcoming",
  overstock: "No overstock",
  snoozed: "Nothing snoozed",
  not_planned: "Every bought sell item is planned",
  all: "No bought sell items",
};

const FLAG_CHIPS: Partial<Record<FlagCode, [string, "warn" | "neutral" | "danger"]>> = {
  no_vendor: ["No vendor", "danger"],
  no_preferred: ["No preferred vendor", "warn"],
  vendor_archived: ["Vendor archived", "danger"],
  default_lead_time: ["Lead time?", "warn"],
  no_moq: ["MOQ?", "neutral"],
  no_price: ["No price", "warn"],
  new_item: ["New", "neutral"],
  stopped_selling: ["Stopped selling", "neutral"],
  po_late: ["PO late", "danger"],
  po_date_guessed: ["PO date?", "neutral"],
  oversold: ["Oversold", "danger"],
  // Make planning ---
  no_cm_site: ["CM has no location", "warn"],
  ship_split: ["Several ship-tos", "neutral"],
};

const GRID = "grid items-center gap-x-2";
/**
 * The plan's columns (Mitch, 2026-11-03: "the purchasing plan view doesn't allow us to edit the columns or view
 * layout"): each person's own in Columns and saved views (useColumnLayout()), the optional ones hidden until shown. The
 * tick box and photo come first and Why/Snooze last, always.
 */
type PlanCol = LayoutColumn & { width: string; right?: boolean; title?: string };
const PLAN_COLS: PlanCol[] = [
  { key: "item", header: "Item", width: "minmax(220px,2.4fr)" },
  { key: "class", header: "Class", width: "40px", title: "A: top 80% of units sold, B: next 15%, C: the rest" },
  { key: "cover", header: "Cover", width: "96px", title: "Days until projected stock runs out (POs on their dates)" },
  {
    key: "next30",
    header: "Next 30d",
    width: "80px",
    right: true,
    title: "Forecast for the next 30 days (stockout-adjusted, seasonal; overrides and adjustments included)",
  },
  {
    key: "available",
    header: "Available / in",
    width: "112px",
    right: true,
    title: "Available at HHL (on hand less units committed to customer orders, not shipped yet) / on order (POs)",
  },
  { key: "on_hand", header: "On hand", width: "80px", right: true, optional: true, title: "On the shelf at HHL, committed and held-back units too" },
  { key: "safety", header: "Safety stock", width: "88px", right: true, optional: true, title: "The floor the plan keeps stock above" },
  { key: "lead", header: "Lead time", width: "76px", right: true, optional: true, title: "Days from ordering to landing at HHL" },
  { key: "moq", header: "MOQ", width: "72px", right: true, optional: true, title: "The vendor's minimum order" },
  { key: "unit_price", header: "Unit price", width: "88px", right: true, optional: true, title: "At the order quantity" },
  { key: "order_by", header: "Order by", width: "118px" },
  { key: "qty", header: "Order qty", width: "118px", right: true },
  { key: "cost", header: "Cost", width: "96px", right: true },
];
/** The grid for the columns shown (`cols`: keys, comma-separated), each at its dragged width (`widths`: "key:px,…") or its own. */
const gridOf = (cols: string, widths = "") => {
  const dragged = new Map(
    widths
      .split(",")
      .filter(Boolean)
      .map((x) => x.split(":") as [string, string]),
  );
  const width = (k: string) => (dragged.has(k) ? `${dragged.get(k)}px` : (PLAN_COLS.find((c) => c.key === k)?.width ?? "80px"));
  return `24px 32px ${cols.split(",").map(width).join(" ")} 96px`;
};
/** The column header's height: the virtual rows start below it. */
const HEAD_H = 29;
type Entry = { kind: "group"; g: VendorGroup } | { kind: "row"; r: ItemPlanResult; g: VendorGroup };

const vendorLabel = (g: Pick<VendorGroup, "vendor">) => (g.vendor ? g.vendor.short_name || g.vendor.name : "No vendor");

// Which bought items the page shows (?scope=): sell items by default, so parts bought for builds (the Make plan's work)
// don't crowd out what sells.
const SCOPES = [
  ["sell", "Sell items"],
  ["parts", "Build parts"],
  ["all", "All"],
] as const;
type Scope = (typeof SCOPES)[number][0];
const inScope = (r: ItemPlanResult, scope: Scope) => scope === "all" || (scope === "sell") === (r.item.kind === "sell");

export function PurchasingPlanPage() {
  const plan = usePlan();
  const edits = useQtyEdits();
  const unticked = useUnticked();
  const pos = useRows("po");
  const params = useSearch();
  const tabParam = params.get("bucket") as Tab | null;
  const tab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : "order_now";
  const vendorFilter = params.get("vendor") ?? "";
  const scopeParam = params.get("scope");
  // Guessed inputs --- ?guessed=1: only rows the plan guessed an input for; ticks select rows for the bulk bar.
  const guessed = params.get("guessed") === "1";
  // No scope asked for: sell items, unless the item opened (?item=, e.g. from the MCP) is a part, then build parts.
  const opened = plan.byItem.get(params.get("item") ?? "");
  const scope: Scope = SCOPES.some(([s]) => s === scopeParam) ? (scopeParam as Scope) : opened && opened.item.kind !== "sell" ? "parts" : "sell";
  const openId = params.get("item");
  const [query, setQuery] = useState("");
  const [created, setCreated] = useState<Map<string, Made>>(() => new Map());
  const [busy, setBusy] = useState(false);
  // Keyboard --- the row j/k are on, and the vendors opened (j/k skip what's hidden). Vendors start folded (Mitch,
  // 2026-11-03: "Purchasing plan companies should be collapsed by default"); a search, or a list of one, opens them.
  const [cursor, setCursor] = useState<string | null>(null);
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(() => new Set());
  // Table views --- the person's columns and saved views of the plan (its tab, scope and vendor with them)
  const { visible: visibleCols, menus: layoutMenus, widths: colWidths, resizer } = useColumnLayout(PLAN_COLS, "/purchasing:Purchasing plan");
  const cols = visibleCols.map((c) => c.key).join(",");
  // Widths dragged, as text so the rows' memo holds ("key:px,…")
  const widths = Object.entries(colWidths)
    .map(([k, v]) => `${k}:${v}`)
    .join(",");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const stacked = useStacked(headerRef);
  const stripRef = useRef<HTMLElement>(null);
  const stripStacked = useStacked(stripRef);
  const canOrder = can("po.create");

  // A stacked strip's tabs scroll: keep the one showing in view
  // biome-ignore lint/correctness/useExhaustiveDependencies: tab is the trigger
  useEffect(() => {
    if (stripStacked) stripRef.current?.querySelector("[data-active]")?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [stripStacked, tab]);

  // Typed quantities whose item went on a PO (or something on one arrived) since are dropped, so they can't order twice;
  // never while an order is on its way (its instant lines change the supply, and a refusal takes them back).
  useEffect(() => {
    if (!busy) dropStaleEdits(plan);
  }, [plan, busy]);

  // Make planning --- builds are on the Make plan; everything bought (parts for builds too) is here, in its scope.
  const allBought = useMemo(() => plan.results.filter((r) => r.role !== "make"), [plan]);
  const bought = useMemo(() => allBought.filter((r) => inScope(r, scope)), [allBought, scope]);
  // Build parts in this tab while only sell items show: "Build parts: N" points at them.
  const partsHere = useMemo(
    () => (scope === "sell" ? allBought.filter((r) => r.item.kind !== "sell" && (tab === "all" ? r.planned : inBucket(r, tab))).length : 0),
    [allBought, scope, tab],
  );
  const counts = useMemo(() => {
    const out = Object.fromEntries(TABS.map((t) => [t, 0])) as Record<Tab, number>;
    for (const r of bought) {
      if (guessed && !isGuessed(r)) continue;
      for (const t of TABS) if (t === "all" ? r.planned : inBucket(r, t)) out[t] += 1;
    }
    return out;
  }, [bought, guessed]);

  const vendorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of bought) if (r.vendor) seen.set(r.vendor.id, r.vendor.short_name || r.vendor.name);
    return [{ value: "", label: "All vendors" }, ...[...seen].sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }))];
  }, [bought]);

  const inTab = useMemo(() => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    return bought.filter((r) => {
      if (tab === "all" ? !r.planned : !inBucket(r, tab)) return false;
      if (vendorFilter && r.vendor?.id !== vendorFilter) return false;
      if (!terms.length) return true;
      const hay = `${r.item.sku} ${r.item.name} ${r.vendor?.name ?? ""} ${r.vendor?.short_name ?? ""}`.toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
  }, [bought, tab, vendorFilter, query]);
  const guessedHere = useMemo(() => inTab.filter(isGuessed).length, [inTab]);
  const rows = useMemo(() => {
    const list = guessed ? inTab.filter(isGuessed) : inTab.slice();
    if (tab === "overstock") return list.sort((a, b) => (b.coverDays ?? 1e9) - (a.coverDays ?? 1e9));
    return list.sort((a, b) => (a.orderByDay ?? 1e9) - (b.orderByDay ?? 1e9) || b.rate - a.rate);
  }, [inTab, guessed, tab]);

  const picked = useCallback((r: ItemPlanResult) => qtyOf(r, edits) > 0 && !unticked.has(r.item.id), [edits, unticked]);
  // Vendors with the most lines to order first; unticking a line never moves its vendor (so j/k keep their place).
  const groups = useMemo(() => {
    const withQty = (g: VendorGroup) => g.rows.reduce((n, r) => n + (qtyOf(r, edits) > 0 ? 1 : 0), 0);
    return groupByVendor(rows, (r) => (picked(r) ? qtyOf(r, edits) : 0)).sort(
      (a, b) =>
        Number(!a.vendor) - Number(!b.vendor) || withQty(b) - withQty(a) || b.rows.length - a.rows.length || vendorLabel(a).localeCompare(vendorLabel(b)),
    );
  }, [rows, edits, picked]);
  const orderable = guessed ? [] : groups.filter((g) => g.vendor && g.lines > 0);
  // The list as drawn: each vendor's header, then its rows unless folded. Every row in it is what j/k walk.
  const openAll = groups.length === 1 || query.trim() !== "";
  const isOpenGroup = useCallback((key: string) => openAll || unfolded.has(key), [openAll, unfolded]);
  const entries = useMemo<Entry[]>(
    () => groups.flatMap((g): Entry[] => [{ kind: "group", g }, ...(isOpenGroup(g.key) ? g.rows.map((r): Entry => ({ kind: "row", r, g })) : [])]),
    [groups, isOpenGroup],
  );
  const visible = useMemo(() => entries.flatMap((e) => (e.kind === "row" ? [e.r] : [])), [entries]);
  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (entries[i]?.kind === "group" ? 41 : 49),
    overscan: 12,
    scrollMargin: HEAD_H,
    scrollPaddingStart: HEAD_H,
    getItemKey: (i) => {
      const e = entries[i];
      return e.kind === "group" ? `g:${e.g.key}` : e.r.item.id;
    },
  });
  const isChecked = guessed ? (r: ItemPlanResult) => selected.has(r.item.id) : picked;
  const canCheck = guessed ? () => true : (r: ItemPlanResult) => qtyOf(r, edits) > 0;
  const untickedHere = useMemo(() => (guessed ? [] : rows.filter((r) => qtyOf(r, edits) > 0 && unticked.has(r.item.id))), [rows, edits, unticked, guessed]);
  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.item.id)), [rows, selected]);

  // Where each vendor's lines go: its open draft for that ship-to and kind (openDraftFor()), else a new one.
  const targets = useMemo(() => {
    const drafts = pos.filter((p) => p.status === "draft");
    return new Map(
      groups.map((g) => [
        g.key,
        g.vendor && g.kind === "purchase" && drafts.some((p) => p.vendor_id === g.vendor?.id)
          ? openDraftFor({ id: g.vendor.id, default_ship_to_location_id: g.vendor.default_ship_to_location_id ?? null }, g.shipTo, g.kind)
          : null,
      ]),
    );
  }, [groups, pos]);

  const check = useCallback(
    (ids: string[], on: boolean) => {
      if (!guessed) return setTicked(ids, on);
      setSelected((s) => {
        const next = new Set(s);
        for (const id of ids) on ? next.add(id) : next.delete(id);
        return next;
      });
    },
    [guessed],
  );

  const orderGroup = async (g: VendorGroup): Promise<Made | null> => {
    if (!g.vendor) return null;
    const lines = g.rows.flatMap((r) => (picked(r) && r.link ? [{ item: r.item, link: r.link, qty: qtyOf(r, edits), result: r, planQty: r.qty }] : []));
    if (!lines.length) return null;
    const target = targets.get(g.key);
    let made: Made | null = null;
    if (target) {
      const res = await addToDraftPo(target, lines);
      if (res) made = { id: target.id, number: target.number, added: res.added + res.raised, existing: true };
    } else {
      const po = await createDraftPo(g.vendor, lines, { shipTo: g.shipTo, kind: g.kind });
      if (po) made = { ...po, added: 0, existing: false };
    }
    if (made) {
      const m = made;
      setCreated((all) => new Map(all).set(g.key, m));
    }
    return made;
  };
  const madeText = (m: Made, g: VendorGroup) =>
    m.existing
      ? m.added
        ? `${plural(m.added, "line")} added to ${m.number}`
        : `${m.number} already covers ${vendorLabel(g)}'s lines: nothing added`
      : `Draft ${m.number || "PO"} for ${vendorLabel(g)}`;

  const orderOne = async (g: VendorGroup) => {
    setBusy(true);
    const made = await orderGroup(g);
    setBusy(false);
    if (made) toast.ok(made.existing ? madeText(made, g) : `${madeText(made, g)}: ${plural(g.lines, "line")}`);
  };

  const orderAll = async () => {
    setBusy(true);
    const done: string[] = [];
    for (const g of orderable) {
      const made = await orderGroup(g);
      if (made) done.push(madeText(made, g));
    }
    setBusy(false);
    if (done.length) toast.ok(done.join(", "));
  };

  // Keys: / search, Esc closes the detail, j/k move between rows (the detail follows while open), Space ticks, Enter opens.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "Escape" && openId && !paneIsOpen() && !document.querySelector("[data-popover]")) setSearch({ item: null });
      if (isTyping(e.target) || document.querySelector("[data-popover]")) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "j" || e.key === "k") {
        e.preventDefault();
        const at = visible.findIndex((r) => r.item.id === cursor);
        const next = visible[at < 0 ? 0 : Math.min(visible.length - 1, Math.max(0, at + (e.key === "j" ? 1 : -1)))];
        if (!next) return;
        setCursor(next.item.id);
        virtualizer.scrollToIndex(
          entries.findIndex((x) => x.kind === "row" && x.r === next),
          { align: "auto" },
        );
        if (openId) setSearch({ item: next.item.id });
      } else if (e.key === " " && cursor) {
        const r = visible.find((x) => x.item.id === cursor);
        if (!r) return;
        e.preventDefault();
        if (guessed) check([r.item.id], !selected.has(r.item.id));
        else if (qtyOf(r, edits) > 0) check([r.item.id], unticked.has(r.item.id));
      } else if (e.key === "Enter" && cursor && visible.some((r) => r.item.id === cursor)) {
        e.preventDefault();
        setSearch({ item: openId === cursor ? null : cursor });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const totals = orderable.reduce(
    (t, g) => ({ lines: t.lines + g.lines, cost: t.cost + (g.currency === "USD" ? g.cost : 0), other: t.other || g.currency !== "USD" }),
    { lines: 0, cost: 0, other: false },
  );
  // The main button says where the lines go: onto the vendors' open drafts, or new ones.
  const adding = orderable.filter((g) => targets.get(g.key));
  const mainLabel =
    orderable.length === 1
      ? adding.length
        ? `Add ${plural(totals.lines, "line")} to ${draftName(targets.get(adding[0].key) as Po)}`
        : `Create draft PO · ${plural(totals.lines, "line")}`
      : !adding.length
        ? `Create ${orderable.length} draft POs`
        : adding.length === orderable.length
          ? `Add ${plural(totals.lines, "line")} to ${adding.length} drafts`
          : `Add to ${plural(adding.length, "draft")}, create ${orderable.length - adding.length}`;
  const mainTitle = orderable
    .map((g) => {
      const t = targets.get(g.key);
      return `${vendorLabel(g)}: ${plural(g.lines, "line")} → ${t ? draftName(t) : "a new draft"}`;
    })
    .join("\n");

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        {/* One row while it fits (the totals truncate first), the main button last like a list page's New; else the totals
            and the other actions get a row of their own (order-last) under the main button (useStacked, as on list pages) */}
        <header
          ref={headerRef}
          className={cx("flex shrink-0 items-center gap-3 border-b border-line bg-surface px-4", stacked ? "flex-wrap gap-y-2 py-2" : "h-12")}
        >
          <h1 className="text-[15px] font-semibold whitespace-nowrap">Purchasing plan</h1>
          <span className="-ml-1 flex shrink-0 items-center gap-1">{layoutMenus}</span>
          <PageSearch
            ref={searchRef}
            value={query}
            onChange={setQuery}
            placeholder="Filter by SKU, name, vendor…"
            className={cx("ml-2", stacked ? "max-w-52 min-w-28 grow basis-28" : "w-52 shrink-0")}
          />
          <div className="w-40 shrink-0">
            <Select value={vendorFilter} options={vendorOptions} onChange={(e) => setSearch({ vendor: e.target.value || null })} />
          </div>
          <div className={cx("flex min-w-0 items-center gap-2", stacked ? "order-last basis-full flex-wrap" : "ml-auto")}>
            {(totals.lines > 0 || untickedHere.length > 0) && (
              <span className={cx("min-w-20 truncate text-[12px] whitespace-nowrap text-muted", openId && "hidden 2xl:inline")} title={mainTitle || undefined}>
                {totals.lines > 0 &&
                  `${plural(totals.lines, "line")}${totals.cost ? ` · ${money(cents(totals.cost))}${totals.other ? " + other currencies" : ""}` : ""}`}
                {untickedHere.length > 0 && (
                  <button
                    type="button"
                    className="ml-1 text-accent-text hover:underline"
                    title="Unticked lines stay unticked in this browser for a week. Click to tick them again."
                    onClick={() =>
                      setTicked(
                        untickedHere.map((r) => r.item.id),
                        true,
                      )
                    }
                  >
                    · {fmt(untickedHere.length)} unticked
                  </button>
                )}
              </span>
            )}
            <AutoRunButton plan={plan} />
            <CoverThrough vendor={null} />
            <ClosuresButton />
            <SettingsButton plan={plan} />
          </div>
          {canOrder && !guessed && (
            <Button
              variant="primary"
              icon={<FilePlus2 className="size-4" />}
              disabled={!orderable.length || busy}
              title={mainTitle || undefined}
              onClick={orderAll}
              className={stacked ? "ml-auto" : "-ml-1"}
            >
              {orderable.length ? mainLabel : "Create draft PO"}
            </Button>
          )}
        </header>
        {/* The bucket tabs with build parts, Guessed and the scope beside them; where they don't fit, those get a row of
            their own above the tabs (useStacked; the tabs keep their underline on the border) and the tabs scroll */}
        <nav ref={stripRef} className={cx("flex shrink-0 border-b border-line bg-surface px-3", stripStacked && "flex-wrap")}>
          <div className={cx("-mb-px flex gap-1", stripStacked ? "min-w-0 basis-full overflow-x-auto" : "shrink-0")}>
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                title={TAB_HINTS[t]}
                data-active={tab === t || undefined}
                onClick={() => setSearch({ bucket: t === "order_now" ? null : t })}
                className={cx(
                  "flex items-center gap-1.5 border-b-2 px-1 py-2 text-[12px] whitespace-nowrap",
                  tab === t ? "border-accent text-text" : "border-transparent text-muted hover:text-text",
                )}
              >
                {TAB_LABELS[t]}
                <span
                  className={cx(
                    "rounded px-1 text-[11px] tabular-nums",
                    t === "order_now" && counts[t]
                      ? "bg-danger-soft text-danger"
                      : t === "at_risk" && counts[t]
                        ? "bg-warn-soft text-warn"
                        : "bg-surface-2 text-faint",
                  )}
                >
                  {fmt(counts[t])}
                </span>
              </button>
            ))}
          </div>
          <div className={cx("flex shrink-0 items-center gap-2 py-1.5", stripStacked ? "order-first basis-full flex-wrap" : "ml-auto pl-2")}>
            {partsHere > 0 && (
              <Link
                to="/make?tab=consignment"
                className="text-[12px] whitespace-nowrap text-accent-text hover:underline"
                title="Parts bought for builds, in this tab: open the Make plan. Its Parts to send lists what each build needs and by when; Build parts shows them here."
              >
                {fmt(partsHere)} build parts<span className="hidden 2xl:inline"> → Make plan</span>
              </Link>
            )}
            <button
              type="button"
              aria-pressed={guessed}
              title="Rows where the plan guessed an input (the default lead time, no MOQ, no price, no vendor, a PO with no date). Fill them in from each chip, or tick several and use the bar."
              onClick={() => {
                setSearch({ guessed: guessed ? null : "1" });
                setSelected(new Set());
              }}
              className={cx(
                "flex h-7 items-center gap-1.5 rounded-md border px-2 text-[12px] whitespace-nowrap",
                guessed ? "border-warn bg-warn-soft font-medium text-warn" : "border-line text-muted hover:bg-hover hover:text-text",
              )}
            >
              <Wand2 className="size-3.5" /> Guessed <span className="tabular-nums">{fmt(guessedHere)}</span>
            </button>
            <div className="flex rounded-md border border-line p-0.5" title="Which bought items to show">
              {SCOPES.map(([s, label]) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={scope === s}
                  // A vendor picked in another scope may have nothing here: the filter goes with it.
                  onClick={() => setSearch({ scope: s === "sell" ? null : s, vendor: null })}
                  className={cx(
                    "h-6 rounded px-2 text-[12px] whitespace-nowrap",
                    scope === s ? "bg-selected font-medium text-text" : "text-muted hover:bg-hover hover:text-text",
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </nav>
        {guessed && <BulkBar rows={selectedRows} today={plan.today} onDone={() => setSelected(new Set())} />}
        <CutoverBanner />
        <StockStaleBanner />
        {!plan.hasSales && (
          <div className="flex items-center gap-2 border-b border-line bg-warn-soft px-4 py-2 text-[12px] text-warn">
            <AlertTriangle className="size-4" /> Sales haven't been pulled from Shopify yet, so nothing has a sales rate. Settings → Shopify → Sales.
          </div>
        )}
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto bg-surface">
          {!groups.length ? (
            <EmptyState
              icon={<ShoppingCart className="size-8" />}
              title={guessed && !query && !vendorFilter ? "No guessed inputs in this tab" : query || vendorFilter ? "Nothing matches" : EMPTY[tab]}
            >
              {tab === "order_now" && !guessed && !query && !vendorFilter && counts.this_month > 0 && (
                <button type="button" className="text-accent-text hover:underline" onClick={() => setSearch({ bucket: "this_month" })}>
                  {counts.this_month} due this month
                </button>
              )}
            </EmptyState>
          ) : (
            <div className="min-w-[1100px]">
              <div
                style={{ height: HEAD_H, gridTemplateColumns: gridOf(cols, widths) }}
                className={cx(GRID, "sticky top-0 z-10 box-border border-b border-line bg-surface-2 pr-4 pl-8 text-[11px] font-medium text-faint")}
              >
                <span />
                <span />
                {visibleCols.map((c, i) => (
                  // A line between headings, in the gap between columns (as DataTable draws them), where a width drags
                  <span key={c.key} className={cx("relative flex min-w-0", c.right && "justify-end")} title={c.title}>
                    <span className="truncate">{c.header}</span>
                    {i < visibleCols.length - 1 && <span aria-hidden className="pointer-events-none absolute inset-y-1.5 -right-1 w-px bg-line-strong" />}
                    {resizer(c.key)}
                  </span>
                ))}
                <span />
              </div>
              {/* Virtualized (N14): only the rows on screen are drawn, so the All tab at 5k items switches at once. Rows
                  measure themselves (a B2B or "at CMs" line makes one taller). */}
              <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
                {virtualizer.getVirtualItems().map((v) => {
                  const e = entries[v.index];
                  return (
                    <div
                      key={e.kind === "group" ? `g:${e.g.key}` : e.r.item.id}
                      data-index={v.index}
                      ref={virtualizer.measureElement}
                      className="absolute inset-x-0 top-0"
                      style={{ transform: `translateY(${v.start - HEAD_H}px)` }}
                    >
                      {e.kind === "group" ? (
                        <VendorHeader
                          group={e.g}
                          plan={plan}
                          isChecked={isChecked}
                          canCheck={canCheck}
                          check={check}
                          open={isOpenGroup(e.g.key)}
                          setOpen={(on) =>
                            setUnfolded((f) => {
                              const next = new Set(f);
                              on ? next.add(e.g.key) : next.delete(e.g.key);
                              return next;
                            })
                          }
                          made={created.get(e.g.key)}
                          target={guessed ? undefined : targets.get(e.g.key)}
                          canOrder={canOrder && !guessed}
                          busy={busy}
                          onDraft={() => orderOne(e.g)}
                        />
                      ) : (
                        <Row
                          r={e.r}
                          plan={plan}
                          qty={qtyOf(e.r, edits)}
                          edited={isEdited(e.r, edits)}
                          typedAt={edits.get(e.r.item.id)?.at}
                          checked={isChecked(e.r)}
                          checkable={canCheck(e.r)}
                          active={openId === e.r.item.id}
                          atCursor={cursor === e.r.item.id}
                          setCursor={setCursor}
                          check={check}
                          cols={cols}
                          widths={widths}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center px-4 py-3 text-[11px] text-faint">
                <span>
                  <Kbd>j</Kbd> <Kbd>k</Kbd> move · <Kbd>Space</Kbd> {guessed ? "selects" : "ticks"} · <Kbd>Enter</Kbd> opens · <Kbd>/</Kbd> filters
                </span>
                {/* Copy / CSV: this tab's rows by vendor, as on screen (the quantities typed over the plan's included). */}
                <ExportButtons name={`Purchasing plan ${TAB_LABELS[tab]}`} className="ml-auto" rows={() => planTable(groups, edits, unticked, plan.today)} />
              </div>
            </div>
          )}
        </div>
      </div>
      {openId && <ItemDetail id={openId} />}
    </div>
  );
}

/** The tab as a table for Copy / CSV: one row per item, its vendor's order the way the screen shows it. */
function planTable(groups: VendorGroup[], edits: ReadonlyMap<string, QtyEdit>, unticked: ReadonlyMap<string, number>, today: string): unknown[][] {
  const head = [
    "Vendor",
    "SKU",
    "Item",
    "Class",
    "Cover (days)",
    "Next 30d",
    "Available",
    "On order",
    "Order by",
    "Order qty",
    "Ticked",
    "Unit price",
    "Cost",
    "Currency",
    "Bucket",
    "Guessed inputs",
  ];
  const rows = groups.flatMap((g) =>
    g.rows.map((r) => {
      const q = qtyOf(r, edits);
      const price = r.terms && q > 0 ? priceFor(r.terms, q) : null;
      return [
        vendorLabel(g),
        r.item.sku,
        r.item.name,
        r.cls ?? "",
        r.coverDays === null ? "" : Math.round(r.coverDays),
        Math.round(r.next30),
        r.available,
        r.incomingQty,
        r.orderByDay === null ? "" : addPlanDays(today, r.orderByDay),
        q,
        q > 0 && !unticked.has(r.item.id) ? "yes" : "",
        price ?? "",
        price != null ? cents(q * price) : "",
        r.currency,
        BUCKET_LABELS[r.bucket],
        r.flags
          .filter((f) => GUESSED.has(f.code))
          .map((f) => FLAG_CHIPS[f.code]?.[0] ?? f.code)
          .join(", "),
      ];
    }),
  );
  return [head, ...rows];
}

/** A draft the plan's lines went to: made now, or an open one they were added to (`added`: lines added or raised). */
type Made = { id: string; number: string; added: number; existing: boolean };
const draftName = (po: Pick<Po, "number" | "auto_key">) => `${po.number} (${po.auto_key ? "auto draft" : "draft"})`;

function VendorHeader({
  group: g,
  plan,
  isChecked,
  canCheck,
  check,
  open,
  setOpen,
  made,
  target,
  canOrder,
  busy,
  onDraft,
}: {
  group: VendorGroup;
  plan: Plan;
  isChecked: (r: ItemPlanResult) => boolean;
  canCheck: (r: ItemPlanResult) => boolean;
  check: (ids: string[], on: boolean) => void;
  open: boolean;
  setOpen: (on: boolean) => void;
  made: Made | undefined;
  target: Po | null | undefined;
  canOrder: boolean;
  busy: boolean;
  onDraft: () => void;
}) {
  const checkable = g.rows.filter(canCheck);
  const allChecked = checkable.length > 0 && checkable.every(isChecked);
  const leadTimes = [...new Set(g.rows.map((r) => r.leadTime))].sort((a, b) => a - b);
  const closed = g.rows.filter((r) => r.delay).length; // Vendor closures ---
  return (
    <div className="flex items-center gap-2 border-t border-line bg-surface px-4 py-2">
      <input
        type="checkbox"
        aria-label={`Pick every line for ${vendorLabel(g)}`}
        checked={allChecked}
        disabled={!checkable.length}
        onChange={(e) =>
          check(
            checkable.map((r) => r.item.id),
            e.target.checked,
          )
        }
        className="accent-[var(--accent)]"
      />
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 font-semibold hover:text-accent-text">
        {open ? <ChevronDown className="size-4 text-faint" /> : <ChevronRight className="size-4 text-faint" />}
        {vendorLabel(g)}
      </button>
      {g.shipTo && g.shipTo !== plan.mrp?.hhl && plan.mrp?.names.get(g.shipTo) && <Badge tone="accent">→ {plan.mrp.names.get(g.shipTo)}</Badge>}
      {g.vendor && (
        <PaneLink kind="vendor" id={g.vendor.id} to={`/vendors?open=${g.vendor.id}`} className="text-faint hover:text-text" title="Open vendor">
          <ExternalLink className="size-3.5" />
        </PaneLink>
      )}
      <span className="text-[12px] text-muted">
        {g.rows.length} item{g.rows.length === 1 ? "" : "s"}
        {leadTimes.length > 0 && ` · ${leadTimes.length > 1 ? `${leadTimes[0]}–${leadTimes.at(-1)}` : leadTimes[0]} day lead`}
        {closed > 0 && ` · ${closed} moved by a closure`}
      </span>
      {g.vendor && <CoverThrough vendor={g.vendor} compact />}
      <div className="ml-auto flex items-center gap-3 text-[12px]">
        {canOrder && g.lines > 0 && (
          <span className="text-muted">
            <span className="font-medium text-text">
              {g.lines} line{g.lines === 1 ? "" : "s"} · {fmt(g.units)} units
            </span>
            {g.cost > 0 && ` · ${money(cents(g.cost), g.currency)}`}
            {g.unpriced > 0 && <span className="text-warn"> · {g.unpriced} unpriced</span>}
          </span>
        )}
        {canOrder && g.moqLines > 0 && (
          <span className="text-muted" title="Units the vendors' MOQs and order multiples added to what's needed">
            MOQ +{fmt(g.moqUnits)} on {g.moqLines} line{g.moqLines === 1 ? "" : "s"}
          </span>
        )}
        {made && (
          <PaneLink kind="po" id={made.id} to={poPath(made.id)} className="text-accent-text hover:underline">
            {made.existing ? `Added to ${made.number}` : `${made.number || "Draft PO"} created`}
          </PaneLink>
        )}
        {canOrder && (
          <Button
            size="sm"
            icon={<FilePlus2 className="size-3.5" />}
            disabled={!g.vendor || !g.lines || busy}
            title={
              !g.vendor
                ? "Link these items to a vendor first"
                : target
                  ? `Adds the picked lines to ${draftName(target)}, ${vendorLabel(g)}'s open draft for this ship-to (an item already on it gets its quantity raised)`
                  : `Starts a draft PO to ${vendorLabel(g)} with the picked lines`
            }
            onClick={onDraft}
          >
            {target ? `Add to ${target.number}` : "Draft PO"}
          </Button>
        )}
      </div>
    </div>
  );
}

const Row = memo(function Row({
  r,
  plan,
  qty,
  edited,
  typedAt,
  checked,
  checkable,
  active,
  atCursor,
  setCursor,
  check,
  cols,
  widths,
}: {
  /** The columns shown, by key, comma-separated (a string, so the memo holds). */
  cols: string;
  /** Their widths dragged, "key:px,…" (likewise). */
  widths: string;
  r: ItemPlanResult;
  plan: Plan;
  qty: number;
  edited: boolean;
  typedAt: number | undefined;
  checked: boolean;
  checkable: boolean;
  active: boolean;
  atCursor: boolean;
  setCursor: (id: string) => void;
  check: (ids: string[], on: boolean) => void;
}) {
  const chips = r.flags.filter((f) => FLAG_CHIPS[f.code]);
  // Make planning --- what builds take of it in the next 30 days.
  const builds30 = Math.round(r.dependent.reduce((n, x) => n + (x.day < 30 ? x.qty : 0), 0));
  const price = r.terms ? priceFor(r.terms, qty) : null;
  const cost = qty > 0 && price != null ? qty * price : null;
  const late = r.orderByDay !== null && r.orderByDay < 0;
  const open = () => setSearch({ item: active ? null : r.item.id });
  // Each column's cell, drawn in the person's order of columns shown.
  const cells: Record<string, ReactNode> = {
    item: (
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-mono text-[12px] font-medium">{r.item.sku || "No SKU"}</span>
          {r.atRisk && r.bucket !== "at_risk" && <Badge tone="warn">At risk</Badge>}
          {r.bucket !== "order_now" && r.bucket !== "at_risk" && r.bucket !== "this_month" && r.bucket !== "upcoming" && (
            <Badge tone={BUCKET_TONE[r.bucket]}>{BUCKET_LABELS[r.bucket]}</Badge>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-1">
          <span className="truncate text-[12px] text-muted" title={r.item.name}>
            {r.item.name}
          </span>
          {chips.map((f) => {
            const [label, tone] = FLAG_CHIPS[f.code] as [string, "warn" | "neutral" | "danger"];
            return <FlagChip key={f.code} r={r} flag={f} plan={plan} label={label} tone={tone} />;
          })}
        </div>
      </div>
    ),
    class: <span className="text-[12px] text-muted">{r.cls ?? "—"}</span>,
    cover: (
      <span className={cx("text-[12px] tabular-nums", r.coverDays === 0 ? "font-medium text-danger" : (r.coverDays ?? 1e9) <= r.landsOn(0) ? "text-warn" : "")}>
        {coverText(r)}
      </span>
    ),
    next30: (
      <span className="text-right font-mono text-[12px]">
        {r.item.kind === "sell" || !r.dependent.length ? (
          <MonthsList r={r} />
        ) : (
          <span title="Parts have no sales: what builds take in the next 30 days">{fmt(builds30)}</span>
        )}
        {r.months.some((m) => m.override) && <span className="ml-0.5 text-accent-text">*</span>}
        {r.b2bOpen > 0 && <span className="block text-[10px] text-muted">+{fmt(r.b2bOpen)} B2B</span>}
        {r.dependent.length > 0 && (r.item.kind !== "sell" || builds30 > 0) && (
          <span className="block text-[10px] text-muted" title="Builds that use it, in the next 30 days (on the day each starts)">
            {r.item.kind === "sell" ? `+${fmt(builds30)} builds` : "for builds"}
          </span>
        )}
      </span>
    ),
    available: (
      <span className="text-right font-mono text-[12px]">
        <StockNumber
          itemId={r.item.id}
          className={cx(r.available !== r.onHand && "underline decoration-dotted underline-offset-2", r.available < 0 && "text-danger")}
          title={stockNote(r) ?? undefined}
        >
          {fmt(r.available)}
        </StockNumber>
        <span className="text-faint"> / </span>
        <IncomingList r={r} today={plan.today} />
        {Math.round(r.atSites) > 0 && (
          <span className="block text-[10px] text-muted" title="At the CMs that build with it: it covers their builds, not HHL's sales">
            +{fmt(Math.round(r.atSites))} at CMs
          </span>
        )}
      </span>
    ),
    order_by: (
      <span
        className={cx(
          "text-[12px]",
          late ? "font-medium text-danger" : r.orderByDay !== null && r.orderByDay <= plan.settings.review_days ? "font-medium" : "text-muted",
        )}
      >
        {orderByText(r, plan.today) || "—"}
        <ClosureChip delay={r.delay} who={r.vendor ? r.vendor.short_name || r.vendor.name : undefined} />
      </span>
    ),
    qty: <QtyCell r={r} qty={qty} edited={edited} typedAt={typedAt} />,
    cost: (
      <span className="text-right font-mono text-[12px]">
        {cost != null ? money(Math.round(cost * 100) / 100, r.currency) : qty > 0 ? <span className="text-faint">no price</span> : ""}
        {r.priceHint && !edited && (
          <button
            type="button"
            className="block w-full text-right text-[10px] text-accent-text hover:underline"
            title={`+${fmt(r.priceHint.extra)} reaches the ${fmt(r.priceHint.qty)} price (${money(r.priceHint.unitPrice, r.currency)} vs ${money(r.priceHint.currentUnitPrice, r.currency)}): saves ${money(r.priceHint.saves, r.currency)}. Click to order ${fmt(r.priceHint.qty)}.`}
            onClick={() => setQtyEdit(r, r.priceHint?.qty ?? null)}
          >
            +{fmt(r.priceHint.extra)} → {money(r.priceHint.unitPrice, r.currency)}
          </button>
        )}
      </span>
    ),
    on_hand: <span className="text-right font-mono text-[12px]">{fmt(r.onHand)}</span>,
    safety: <span className="text-right font-mono text-[12px]">{fmt(Math.ceil(r.safetyStock))}</span>,
    lead: <span className="text-right text-[12px] text-muted">{r.leadTime} days</span>,
    moq: <span className="text-right font-mono text-[12px]">{r.moq ? fmt(r.moq) : <span className="text-faint">—</span>}</span>,
    unit_price: <span className="text-right font-mono text-[12px]">{price != null ? money(price, r.currency) : <span className="text-faint">—</span>}</span>,
  };
  return (
    <div
      data-row={r.item.id}
      style={{ gridTemplateColumns: gridOf(cols, widths) }}
      className={cx(
        GRID,
        // Under its vendor: further in than the vendor's name, on a line down from the vendor's tick box (the column
        // header is as far in, so the columns line up).
        "group relative scroll-mt-8 border-t border-line/60 py-1.5 pr-4 pl-8 hover:bg-hover",
        "before:absolute before:inset-y-0 before:left-[22px] before:w-px before:bg-line",
        active && "bg-accent-soft/40",
        atCursor && "shadow-[inset_3px_0_0_var(--accent)]",
      )}
      onClick={(e) => {
        setCursor(r.item.id);
        if (!(e.target as HTMLElement).closest("button,input,label,a,[data-popover]")) open();
      }}
    >
      {/* The whole cell ticks, so a near miss never opens the detail instead. */}
      <label className="-my-1.5 -ml-8 flex h-[calc(100%+12px)] cursor-pointer items-center pl-8">
        <input
          type="checkbox"
          aria-label={`Pick ${r.item.sku}`}
          checked={checked}
          disabled={!checkable}
          onChange={(e) => check([r.item.id], e.target.checked)}
          className="accent-[var(--accent)]"
        />
      </label>
      <Thumb src={r.item.photo_url} size={32} />
      {cols.split(",").map((k) => (
        <Fragment key={k}>{cells[k]}</Fragment>
      ))}
      <span className="flex justify-end gap-0.5">
        <WhyButton
          r={r}
          plan={plan}
          width={420}
          badges={
            <>
              <Badge tone={BUCKET_TONE[r.bucket]}>{BUCKET_LABELS[r.bucket]}</Badge>
              {r.cls && <Badge>Class {r.cls}</Badge>}
            </>
          }
        />
        <SnoozeButton r={r} today={plan.today} />
        <HideButton r={r} />
      </span>
    </div>
  );
});

function QtyCell({ r, qty, edited, typedAt }: { r: ItemPlanResult; qty: number; edited: boolean; typedAt: number | undefined }) {
  const [draft, setDraft] = useState<string | null>(null);
  // Escape drops what was typed: the blur it causes must not save it.
  const escaped = useRef(false);
  const warnings = r.terms && qty > 0 ? qtyWarnings(r.terms, qty) : [];
  const commit = (text: string) => {
    setDraft(null);
    const t = text.replace(/[,\s]/g, "");
    if (t === "") return setQtyEdit(r, null);
    const n = numberOf(text);
    if (!Number.isFinite(n) || n < 0) return toast.error("Enter a quantity");
    setQtyEdit(r, n === r.qty ? null : n);
  };
  // Virtualized --- a row scrolled out of the list unmounts without a blur: what was typed is kept all the same.
  const pending = useRef<{ text: string | null; r: ItemPlanResult }>({ text: null, r });
  pending.current = { text: draft, r };
  useEffect(
    () => () => {
      const { text, r: at } = pending.current;
      if (text === null) return;
      const t = text.replace(/[,\s]/g, "");
      const n = numberOf(text);
      if (t !== "" && Number.isFinite(n) && n >= 0) setQtyEdit(at, n === at.qty ? null : n);
    },
    [],
  );
  return (
    <span className="flex items-center justify-end gap-1">
      {warnings.length > 0 && (
        <button
          type="button"
          title={`${warnings.map((w) => w.message).join("; ")}. Click for ${fmt(warnings[0].fix)}.`}
          onClick={() => setQtyEdit(r, warnings[0].fix)}
        >
          <AlertTriangle className="size-3.5 text-warn" />
        </button>
      )}
      {edited && (
        <button
          type="button"
          title={`Typed ${typedAt ? dayText(new Date(typedAt).toISOString()) : ""}; kept in this browser until it goes on a PO. Back to the suggestion (${fmt(r.qty)})`}
          onClick={() => setQtyEdit(r, null)}
          className="text-faint hover:text-text"
        >
          <RotateCcw className="size-3" />
        </button>
      )}
      <input
        data-qty
        value={draft ?? (qty ? String(qty) : "")}
        placeholder="0"
        inputMode="numeric"
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => {
          if (escaped.current) escaped.current = false;
          else if (draft !== null) commit(e.currentTarget.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            escaped.current = true;
            pending.current = { text: null, r };
            setDraft(null);
            e.currentTarget.blur();
          }
          if (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const all = [...document.querySelectorAll<HTMLInputElement>("input[data-qty]")];
            const next = all[all.indexOf(e.currentTarget) + (e.key === "ArrowUp" ? -1 : 1)];
            if (next) next.focus();
            else e.currentTarget.blur();
          }
        }}
        title={r.rawQty !== r.qty ? `Needed ${fmt(r.rawQty)}; rounded to the vendor's MOQ / order multiple` : undefined}
        className={cx(
          "h-7 w-20 rounded-md border bg-surface px-2 text-right font-mono text-[12px] focus:border-accent focus:outline-none",
          edited ? "border-accent text-accent-text" : "border-line hover:border-line-strong",
        )}
      />
    </span>
  );
}

// Popovers -----------------------------------------------------------------------------------------------
// Cross-links --- what used to be hover-only lists: click for the POs on their way (each opening its PO) and the
// forecast by month (with the way to the Forecast page).

function IncomingList({ r, today }: { r: ItemPlanResult; today: string }) {
  if (!r.incoming.length) return <span className="text-faint">{fmt(r.incomingQty)}</span>;
  return (
    <Popover
      width={320}
      button={(toggle) => (
        <button
          type="button"
          onClick={toggle}
          className="underline decoration-dotted underline-offset-2 hover:text-accent-text"
          title="On POs: click for which"
        >
          {fmt(r.incomingQty)}
        </button>
      )}
    >
      {() => (
        <div className="text-[12px]">
          <div className="mb-1 font-medium">On order · {r.item.sku}</div>
          {r.incoming.map((x) => (
            <PaneLink kind="po" id={x.poId} key={x.lineId} to={poPath(x.poId)} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-hover">
              <span className="w-16 shrink-0 font-mono font-medium">{x.poNumber}</span>
              <span className="flex-1 text-muted">
                {x.draft ? "Draft" : x.late ? <span className="text-danger">Past due</span> : `Lands ${shortDate(x.arrive, today)}`}
              </span>
              <span className="font-mono">{fmt(x.qty)}</span>
            </PaneLink>
          ))}
        </div>
      )}
    </Popover>
  );
}

function MonthsList({ r }: { r: ItemPlanResult }) {
  return (
    <Popover
      width={260}
      button={(toggle) => (
        <button type="button" onClick={toggle} className="hover:text-accent-text" title="Forecast by month: click for the months">
          {fmt(Math.round(r.next30))}
        </button>
      )}
    >
      {() => (
        <div className="text-[12px]">
          <div className="mb-1 font-medium">Forecast · {r.item.sku}</div>
          {r.months.map((m) => (
            <div key={m.month} className="flex items-center gap-2 px-1.5 py-0.5">
              <span className="flex-1 text-muted">{monthLabel(m.month)}</span>
              {m.override && <span className="text-[11px] text-accent-text">override</span>}
              <span className="font-mono">{fmt(Math.round(m.units))}</span>
            </div>
          ))}
          <Link to={`/forecast?item=${r.item.id}`} className="mt-1 block px-1.5 text-accent-text hover:underline">
            Open in Forecast
          </Link>
        </div>
      )}
    </Popover>
  );
}

function SnoozeButton({ r, today }: { r: ItemPlanResult; today: string }) {
  if (!can("item_plan.update") || !r.planned) return <span className="w-6" />;
  if (r.snoozedUntil) {
    return (
      <button
        type="button"
        title={`Snoozed until ${r.snoozedUntil}: ${r.snoozeReason}. Click to unsnooze.`}
        onClick={() => saveItemPlan(r.item.id, { snooze_until: null, snooze_reason: "" })}
        className="rounded p-1 text-accent-text hover:bg-hover"
      >
        <Moon className="size-3.5 fill-current" />
      </button>
    );
  }
  return (
    <Popover
      button={(toggle, open) => (
        <button
          type="button"
          title="Snooze"
          onClick={toggle}
          className={cx("rounded p-1 hover:bg-hover hover:text-text", open ? "text-text" : "text-faint opacity-0 group-hover:opacity-100 focus:opacity-100")}
        >
          <Moon className="size-3.5" />
        </button>
      )}
    >
      {(close) => (
        <>
          <div className="mb-2 text-[12px] font-medium">Snooze {r.item.sku}</div>
          <SnoozeForm itemId={r.item.id} today={today} onDone={close} />
        </>
      )}
    </Popover>
  );
}

/**
 * Hidden from the plan (Mitch, 2026-11-03: "Being able to hide items in the purchasing plan, if it's discontinued … a way
 * to go view those items later to un-hide them"): set to not planned with why (item_plan.policy none, hidden_reason), so
 * it's in the Not planned tab, where its row shows it again (back to Suggest, I order).
 */
const HIDE_REASONS = ["Discontinued", "Not stocking it any more", "Sold out for good"];

function HideButton({ r }: { r: ItemPlanResult }) {
  const [why, setWhy] = useState("");
  if (!can("item_plan.update")) return null;
  if (!r.planned) {
    if (r.policy !== "none") return <span className="w-6" />;
    return (
      <button
        type="button"
        title={`${r.skip}. Click to plan it again.`}
        onClick={async () => {
          if (await saveItemPlan(r.item.id, { policy: "manual", hidden_reason: "" })) toast.ok(`${r.item.sku || r.item.name} is planned again`);
        }}
        className="flex items-center gap-1 rounded px-1 py-0.5 text-[12px] text-accent-text hover:bg-hover"
      >
        <Eye className="size-3.5" /> Show again
      </button>
    );
  }
  const hide = async (reason: string, close: () => void) => {
    if (!(await saveItemPlan(r.item.id, { policy: "none", hidden_reason: reason.trim() }))) return;
    close();
    toast.ok(`${r.item.sku || r.item.name} hidden: it's in Not planned, where Show again brings it back`);
  };
  return (
    <Popover
      width={300}
      button={(toggle, open) => (
        <button
          type="button"
          title="Hide from the plan (discontinued…)"
          onClick={toggle}
          className={cx("rounded p-1 hover:bg-hover hover:text-text", open ? "text-text" : "text-faint opacity-0 group-hover:opacity-100 focus:opacity-100")}
        >
          <EyeOff className="size-3.5" />
        </button>
      )}
    >
      {(close) => (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void hide(why, close);
          }}
        >
          <div className="text-[12px] font-medium">Hide {r.item.sku} from the plan</div>
          <div className="text-[12px] text-muted">It's never suggested while hidden. The Not planned tab lists it, with Show again.</div>
          <div className="flex flex-wrap gap-1">
            {HIDE_REASONS.map((x) => (
              <Button key={x} size="sm" variant="secondary" onClick={() => void hide(x, close)}>
                {x}
              </Button>
            ))}
          </div>
          <Input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Or say why…" className="h-7 text-[12px]" />
          <div className="flex justify-end">
            <Button size="sm" variant="primary" type="submit">
              Hide
            </Button>
          </div>
        </form>
      )}
    </Popover>
  );
}

function SettingsButton({ plan }: { plan: Plan }) {
  const editable = can("planning_settings.update");
  const s = plan.settings;
  // Cutover --- whether the orders placed before HHL Ops are entered (the plans' note, CutoverBanner).
  const row = { id: "settings", ...s, prior_orders_entered: !!useRows("planning_settings")[0]?.prior_orders_entered };
  const n = (key: keyof typeof s, label: string, unit: string) => ({
    key,
    label,
    type: "number" as const,
    format: (v: unknown) => `${fmt(Number(v))} ${unit}`,
    save: (v: unknown) => (v == null ? toast.error(`${label} can't be empty`) : saveSettings({ [key]: v })),
  });
  return (
    <Popover width={360} button={(toggle) => <Button variant="ghost" icon={<Settings2 className="size-4" />} title="Planning settings" onClick={toggle} />}>
      {() => (
        <>
          <div className="mb-1 font-medium">Planning settings</div>
          <div className="mb-2 text-[12px] text-muted">For every item. An item's own safety stock or target is set in its panel.</div>
          <Fields
            compact
            disabled={!editable}
            row={row}
            fields={[
              n("review_days", "Review every", "days"),
              n("default_lead_time_days", "Default lead", "days"),
              n("cover_days", "Order covers", "days"),
              n("horizon_days", "Upcoming", "days"),
              n("overstock_months", "Overstock", "months"),
              n("service_a", "Class A target", "%"),
              n("service_b", "Class B target", "%"),
              n("service_c", "Class C target", "%"),
              { key: "prior_orders_entered", label: "Old orders in", type: "bool", save: (v: unknown) => saveSettings({ prior_orders_entered: !!v }) },
            ]}
          />
          <div className="mt-2 text-[11px] leading-4 text-faint">
            Order now: order-by within the review period. Orders cover their lead time plus "Order covers" (or the vendor's order cycle). A vendor's own lead
            time and MOQ fill in where its links have none; the default lead is used where neither has one (flagged). Targets set safety stock by class (A = top
            80% of units sold).
          </div>
        </>
      )}
    </Popover>
  );
}

// Detail panel ---------------------------------------------------------------------------------------------

function ItemDetail({ id }: { id: string }) {
  const item = useRow("item", id);
  const plan = usePlan();
  const r = plan.byItem.get(id);
  if (!item) return null;
  return (
    <PlanDetail item={item} about={r?.vendor ? r.vendor.short_name || r.vendor.name : "No vendor"} className="w-[460px]">
      {r ? <ItemPlanSection item={item} inPlan /> : <div className="p-2 text-muted">Not a bought sell item, so it isn't planned here.</div>}
      <ForecastSection item={item} />
      <SalesSection item={item} />
    </PlanDetail>
  );
}
